import React, { useState } from 'react';
import { X, ArrowDownToLine, Monitor } from 'lucide-react';
import { getAppUrl } from '@/utils/validation';
import { isMacOS, getDownloadUrl } from '@/utils/platformUtils';
import { skylarqBrand as brand } from '@/styles/tokens';
import skylarqLogo from '@/assets/skylarq_logo_small.png';

const DISMISS_KEY = 'skylarq_dataset_promo_dismissed';

export const DatasetPromoModal: React.FC<{
  leadCount: number;
  onDismiss: () => void;
}> = ({ leadCount, onDismiss }) => {
  const mac = isMacOS();
  const [ctaHover, setCtaHover] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, 'true');
    onDismiss();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div
        className="relative max-w-md w-full mx-4 p-8 text-center"
        style={{
          backgroundColor: brand.bg,
          borderRadius: '16px',
          boxShadow: '0 24px 80px rgba(0,0,0,0.12), 0 0 0 1px rgba(0,0,0,0.06)',
        }}
      >
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 p-1 rounded-lg transition-colors"
          style={{ color: brand.textFaint }}
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>

        <img
          src={skylarqLogo}
          alt="Skylarq"
          className="w-14 h-14 object-contain mx-auto mb-5"
        />

        <p
          className="text-[12px] font-semibold uppercase tracking-wide mb-3"
          style={{ color: brand.accent }}
        >
          AI sales agent for Mac
        </p>

        <h2
          className="text-[22px] font-bold mb-3 leading-tight"
          style={{ color: brand.text }}
        >
          {leadCount} leads ready for you
        </h2>

        <p className="text-[15px] mb-5 leading-relaxed" style={{ color: brand.textMuted }}>
          {mac
            ? 'Find leads. Run outreach. Close deals. One agent — fully automated.'
            : 'Find leads. Run outreach. Close deals. One platform — fully automated.'}
        </p>

        {mac ? (
          <a
            href={getDownloadUrl()}
            className="flex items-center justify-center gap-2 w-full py-3.5 text-white text-[15px] font-semibold transition-all"
            style={{
              backgroundColor: ctaHover ? brand.accentHover : brand.accent,
              borderRadius: '100px',
              boxShadow: ctaHover ? brand.accentShadow : '0 8px 32px rgba(196,93,53,0.25)',
            }}
            onMouseEnter={() => setCtaHover(true)}
            onMouseLeave={() => setCtaHover(false)}
          >
            <ArrowDownToLine className="w-4 h-4" />
            Download for Mac
          </a>
        ) : (
          <a
            href={getAppUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 w-full py-3.5 text-white text-[15px] font-semibold transition-all"
            style={{
              backgroundColor: ctaHover ? brand.accentHover : brand.accent,
              borderRadius: '100px',
              boxShadow: ctaHover ? brand.accentShadow : '0 8px 32px rgba(196,93,53,0.25)',
            }}
            onMouseEnter={() => setCtaHover(true)}
            onMouseLeave={() => setCtaHover(false)}
          >
            <Monitor className="w-4 h-4" />
            Get Started Free
          </a>
        )}

        <p className="text-[12px] mt-3" style={{ color: brand.textFaint }}>
          {mac
            ? 'Mac only. Free during early access — no credit card required.'
            : 'Free to get started — no credit card required.'}
        </p>

        <button
          onClick={handleDismiss}
          className="mt-3 text-[13px] transition-colors"
          style={{ color: brand.textFaint }}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
};

/**
 * Check if the dataset promo modal has been dismissed before.
 */
export const isDatasetPromoDismissed = (): boolean => {
  return localStorage.getItem(DISMISS_KEY) === 'true';
};
